import React from 'react';
import { err, ok } from 'neverthrow';
import { z } from 'zod/v3';
import {
  BaseAction,
  type ActionResult,
  type ApplicableContext,
  type ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { toast } from '@cotera/client/app/components/ui';
import { ConfirmDestructiveActionModal } from '@cotera/client/app/components/app/confirm-destructive-action-modal';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import type { WorkflowRunActionTarget } from './workflow-run-action-targets.model';
import {
  refreshWorkflowRunViews,
  reportWorkflowRunBatch,
  runNoun,
  workflowRunTargetsFromContext,
  type WorkflowRunActionErrors,
  type WorkflowRunActionsClient,
} from './workflow-run-actions.shared';

type ModalManager = {
  push: (modal: {
    view: () => React.ReactNode;
    onClose?: () => void | Promise<void>;
  }) => void;
  pop: () => void;
};

/**
 * Cancels the in-progress runs currently targeted by a
 * `WorkflowRunActionTargetsModel` — the row action menu, the bulk selection
 * bar, and the run detail header all target through that one model.
 */
export class CancelWorkflowRunsAction extends BaseAction<
  Record<string, never>,
  WorkflowRunActionErrors
> {
  title = 'Cancel runs';
  group = 'Workflow';
  // A circled X, not a bare one: the bare `x` is the dismiss glyph and reads as
  // "close this view" beside a closable panel.
  icon = 'x-circle';
  inputSchema = z.object({});

  /**
   * Snapshot taken when the confirm dialog opens: the list selection can change
   * while the dialog is up, and the cancel must apply to what was confirmed.
   */
  private _pendingRuns: WorkflowRunActionTarget[] | undefined;

  constructor(
    private readonly client: WorkflowRunActionsClient,
    private readonly modalManager: ModalManager
  ) {
    super();
  }

  override applicable(context: ApplicableContext): boolean {
    const targets = workflowRunTargetsFromContext(context);
    return targets !== null && targets.cancellableRuns.length > 0;
  }

  // Cancelling is irreversible and stops work already in flight, so it always
  // confirms. Retry is additive, so it never does.
  shouldAsk(): boolean {
    return true;
  }

  ask(
    _params: Partial<Record<string, never>>,
    context: ExecuteContext
  ): AskResponse<Record<string, never>, WorkflowRunActionErrors> {
    const runs = workflowRunTargetsFromContext(context)?.cancellableRuns ?? [];
    this._pendingRuns = runs;
    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => (
            <ConfirmDestructiveActionModal
              title={`Cancel ${runNoun(runs.length)}`}
              subtitle="Work already in flight stops and cannot be resumed. Cancelled runs can be retried afterwards."
              confirmLabel="Cancel runs"
              onConfirm={async () => {
                this.modalManager.pop();
                await onRespond({});
              }}
              onCancel={async () => {
                this._pendingRuns = undefined;
                this.modalManager.pop();
                await onCancel();
              }}
            />
          ),
          onClose: async () => {
            this._pendingRuns = undefined;
            await onCancel();
          },
        });
      },
    };
  }

  override async execute(
    _params: Record<string, never>,
    context: ExecuteContext
  ): Promise<ActionResult<WorkflowRunActionErrors>> {
    const targets = workflowRunTargetsFromContext(context);
    if (targets === null) {
      return ok({});
    }
    const runs = this._pendingRuns ?? targets.cancellableRuns;
    this._pendingRuns = undefined;
    if (runs.length === 0) {
      return ok({});
    }

    const pending = toast.loading(`Cancelling ${runNoun(runs.length)}...`);
    const results = await Promise.allSettled(
      runs.map((run) =>
        this.client.resource.workflow.cancelRun({
          workflowId: targets.workflowId,
          runId: run.id,
        })
      )
    );
    const succeeded = results.filter(
      (result) => result.status === 'fulfilled' && result.value.isOk()
    ).length;

    reportWorkflowRunBatch({
      pending,
      total: runs.length,
      succeeded,
      allSucceededMessage: (noun) => `Cancelled ${noun}`,
      noneSucceededMessage: (noun) => `Could not cancel ${noun}`,
      partialMessage: (count, noun) => `Cancelled ${count} of ${noun}`,
    });

    // Unconditional: on total failure the refresh is what reconverges the view
    // to server truth.
    refreshWorkflowRunViews(context);

    if (succeeded === 0) {
      return err([{ t: 'error', message: 'Failed to cancel runs' }]);
    }
    return ok({});
  }
}
